import { DestroyReason, DriveStep, driver } from "driver.js";
import "driver.js/dist/driver.css";
import { useCallback, useEffect, useRef, useState } from "react";

/**
 * CLE-3015 PR 0 (spike) — the thinnest possible driver.js wrapper. The real engine
 * (spa/src/training/engine/useTour.ts, PR 2) replaces this; it exists to prove driver.js works in
 * this SPA and under its Jest config.
 *
 * - `allowClose: false` + `overlayClickBehavior: "none"`: Escape and overlay clicks never end the
 *   tour; only the popover's own (React) Exit does, via `destroy`.
 * - `onPopoverRender` appends an empty element to the popover's description and hands it back as
 *   `popoverContainer`, so the caller can `createPortal` React content into it. driver.js builds a
 *   new popover for every step, so the container changes per step and the old portal unmounts.
 * - The driver instance is destroyed on unmount, which also cancels a pending `waitForElement`
 *   (driver clears its timeout and disconnects its MutationObserver).
 */
export interface IUseDriverTourOptions {
  steps: DriveStep[];
  enabled: boolean;
  onDestroyed?: (reason: DestroyReason | undefined) => void;
}

export interface IUseDriverTourResult {
  popoverContainer: HTMLElement | null;
  destroy: () => void;
}

export function useDriverTour({
  steps,
  enabled,
  onDestroyed,
}: IUseDriverTourOptions): IUseDriverTourResult {
  const [popoverContainer, setPopoverContainer] = useState<HTMLElement | null>(
    null
  );
  const driverRef = useRef<ReturnType<typeof driver> | null>(null);
  // Read through refs so a caller passing inline arrays/callbacks does not restart the tour.
  const stepsRef = useRef(steps);
  stepsRef.current = steps;
  const onDestroyedRef = useRef(onDestroyed);
  onDestroyedRef.current = onDestroyed;

  useEffect(() => {
    if (!enabled) return undefined;

    const instance = driver({
      steps: stepsRef.current,
      allowClose: false,
      overlayClickBehavior: "none",
      onPopoverRender: (popover) => {
        const container = document.createElement("div");
        container.className = "tour-spike-react-content";
        popover.description.appendChild(container);
        // Shown even when a step has no description text of its own.
        popover.description.style.display = "block";
        setPopoverContainer(container);
      },
      // driver.js sizes and places the popover inside `onPopoverRender`, before React has
      // committed the caller's portal content into it. A `side: "bottom"` popover is anchored by
      // `style.bottom`, so that late content grows it UPWARD over the highlighted element (Dev QA
      // of #1432), and driver only repositions on resize/scroll. Re-place it once the highlight
      // transition ends (`duration`, 400 ms), by which time the portal has long committed.
      // `refresh()` is a no-op before then: it reads driver's `__activeElement`, set only at the end.
      onHighlighted: () => instance.refresh(),
      onDestroyed: (_element, _step, opts) => {
        setPopoverContainer(null);
        onDestroyedRef.current?.(opts.reason);
      },
    });
    driverRef.current = instance;
    instance.drive();

    return () => {
      instance.destroy();
      if (driverRef.current === instance) driverRef.current = null;
      setPopoverContainer(null);
    };
  }, [enabled]);

  const destroy = useCallback(() => {
    driverRef.current?.destroy();
  }, []);

  return { popoverContainer, destroy };
}
